import React from "react";
import ReactDOM from "react-dom/client";
import "./index.css";
import App from "./App";
import { ApolloProvider } from "@apollo/client";
import { initializeApp } from "firebase/app";
import { User, getAuth, onAuthStateChanged } from "firebase/auth";
import { REDIRECTING_TO_SIGN_IN, signInWithGoogle } from "./auth/signin";
import { client } from "./pages/operations/client";
import { logToServer } from "./shared/utils";
import { addonSignInWithGoogle } from "./auth/addon-signin";

// TODO: Replace the following with your app's Firebase project configuration
// Firebase always opens https://{authDomain}/__/auth/handler. Same-origin
// authDomain (required for getRedirectResult) only works when this page is
// already HTTPS. On http://localhost, fall back to the Firebase-hosted domain.
const firebaseAuthDomain =
  window.location.protocol === "https:"
    ? window.location.host
    : "workflows-414809.firebaseapp.com";

const firebaseConfig = {
  apiKey: "AIzaSyBgWZ96IM4WGCcy8HYzN5vl99UAWbjvYsc",
  authDomain: firebaseAuthDomain,
  projectId: "workflows-414809",
  storageBucket: "workflows-414809.appspot.com",
  messagingSenderId: "416924528129",
  appId: "1:416924528129:web:c96ad19c509c82ae7e9a00",
};

const app = initializeApp(firebaseConfig);

function getCurrentUser(): Promise<User | null> {
  const auth = getAuth(app);
  return new Promise((resolve, reject) => {
    const unsubscribe = onAuthStateChanged(
      auth,
      (user) => {
        unsubscribe(); // Unsubscribe from the listener once we get the user data
        resolve(user);
      },
      reject
    );
  });
}

const root = ReactDOM.createRoot(
  document.getElementById("root") as HTMLElement
);

const AuthenticatedApp = () => (
  <React.StrictMode>
    <ApolloProvider client={client}>
      <App />
    </ApolloProvider>
  </React.StrictMode>
);

function renderSignInPrompt(message?: string) {
  const onClick = () => {
    root.render(<div>Logging in...</div>);
    signInWithGoogle(client)
      .then(() => {
        const currentUrl = new URL(window.location.href);
        currentUrl.searchParams.delete("auth");
        window.location.href = currentUrl.toString();
      })
      .catch((error) => {
        if (error?.message === REDIRECTING_TO_SIGN_IN) {
          root.render(<div>Redirecting to sign-in...</div>);
          return;
        }
        const retryable =
          error?.code === "auth/popup-closed-by-user" ||
          error?.code === "auth/popup-blocked" ||
          error?.code === "auth/cancelled-popup-request";
        if (retryable) {
          renderSignInPrompt(
            "Popup was closed or blocked. Click below to try again."
          );
          return;
        }
        renderSignInPrompt("Authentication error. Please try again.");
      });
  };

  root.render(
    <div style={{ padding: 24, fontFamily: "sans-serif" }}>
      {message ? <p>{message}</p> : <p>Sign in to continue.</p>}
      <button type="button" onClick={onClick}>
        Continue with Google
      </button>
    </div>
  );
}

const getUser = async (client: any) => {
  const queryParams = new URLSearchParams(window.location.search);
  const state = queryParams.get("state") || localStorage.getItem("state");
  logToServer("query params", {
    code: queryParams.get("code"),
    scope: queryParams.get("scope"),
    prompt: queryParams.get("prompt"),
    state: queryParams.get("state"),
  });

  try {
    const user = await getCurrentUser();
    if (state) {
      const decodedState = JSON.parse(atob(state));
      logToServer("Decoded state", decodedState);
      const { token, callbackUrl } = decodedState;
      const redirectUrl = `${callbackUrl}?token=${encodeURIComponent(
        localStorage.getItem("token") || ""
      )}&state=${encodeURIComponent(token)}`;
      await addonSignInWithGoogle(client, app)
        .then(() => {
          logToServer("Redirecting to", { redirectUrl });
          window.location.href = redirectUrl;
        })
        .catch((error) => {
          logToServer("Redirecting to sign-in", error);
          root.render(<div>Redirecting to sign-in.</div>);
        });
    } else if (
      !user ||
      queryParams.get("auth") ||
      sessionStorage.getItem("isSigningIn") ||
      !localStorage.getItem("token")
    ) {
      if (queryParams.get("auth")) {
        // reset token if requested to force authentication
        localStorage.setItem("token", "");
      }
      // On HTTP (local), auto-popup is often blocked — show a button first.
      // On HTTPS (prod), redirect starts immediately from signInWithGoogle.
      if (window.location.protocol !== "https:") {
        renderSignInPrompt();
        return null;
      }

      root.render(<div>Logging in...</div>);
      signInWithGoogle(client)
        .then(() => {
          // After successful sign-in, reload the page
          // remove ?auth=true from url and reload
          const currentUrl = new URL(window.location.href);
          currentUrl.searchParams.delete("auth");
          window.location.href = currentUrl.toString();
        })
        .catch((error) => {
          if (error?.message === REDIRECTING_TO_SIGN_IN) {
            root.render(<div>Redirecting to sign-in...</div>);
            return;
          }
          console.error("Sign-in error:", error);
          renderSignInPrompt("Authentication error. Please try again.");
        });
      return null;
    } else {
      await logToServer("User:", user);
      // Web App
      root.render(<AuthenticatedApp />);
    }
  } catch (error) {
    logToServer("Error in login", {
      error: (error as Error).toString(),
    });
    console.error("Authentication error:", error);
    root.render(<div>Authentication error. Please try again.</div>);
  }
};

getUser(client);
